import { PlanDataTypeEnum } from 'api/client';
import Intro from 'components/intro';
import Layout from 'components/layout';
import SEO from 'components/seo';
import useAuth from 'hooks/use-auth';
import { GetStaticProps, NextPage } from 'next';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { useEffectOnce } from 'react-use';
import AnalyticsService, { ScreenNames } from 'services/analytics';
import { useStore } from 'store';
import { LeaveEarlyIntroModeEnum } from 'types/base';
import { backgroundImages } from 'utils/config';
import { scrollToTop } from 'utils/helpers';
import APP_ROUTES from 'utils/routes';

const PAGE_TITLE = 'Plan to Be safe and Leave Early';
const PAGE_DESCRIPTION = `Leaving early is your safest option during a bushfire emergency. Create your bushfire survival plan in 15 min or less so you know when and how to act.`;

interface Props {}

const LeaveEarlyIntro: NextPage<Props> = () => {
  const { startAnonymousSession, loading, isAuthenticated } = useAuth();
  const set = useStore((state) => state.set);
  const clearPlanData = useStore((state) => state.clearPlanData);
  const router = useRouter();
  const introMode = (router.query.mode ||
    LeaveEarlyIntroModeEnum.Default) as LeaveEarlyIntroModeEnum;

  const onStart = useCallback(() => {
    // Start an anonymous session unless we already have a current session.
    if (!loading && !isAuthenticated) startAnonymousSession();

    clearPlanData();

    set(({ plan }) => {
      plan.draft.type = PlanDataTypeEnum.Leave;
      plan.isDirty = true;
    }, 'setPlanType');

    // Kick off the plan...
    router.push(APP_ROUTES.leave.plan).then(scrollToTop);
  }, [
    loading,
    isAuthenticated,
    startAnonymousSession,
    clearPlanData,
    set,
    router,
  ]);

  useEffectOnce(() => {
    // The user has come from the "Leave early option, or visited the URL directly"
    if (introMode === LeaveEarlyIntroModeEnum.Default) {
      AnalyticsService.trackScreen(ScreenNames.leaveIntro);
    }

    // The user has come from the "Help me decide option"
    if (introMode === LeaveEarlyIntroModeEnum.Help) {
      AnalyticsService.trackScreen(ScreenNames.unsureIntro);
    }
  });

  return (
    <Layout mainBackground={backgroundImages.treesBurn}>
      <SEO title={PAGE_TITLE} description={PAGE_DESCRIPTION} />
      <Intro
        type={PlanDataTypeEnum.Leave}
        introMode={introMode}
        onStart={onStart}
      />
    </Layout>
  );
};

export const getStaticProps: GetStaticProps<Props> = async () => {
  return {
    props: {},
  };
};

export default LeaveEarlyIntro;
